import { useCallback, useEffect, useState } from "preact/hooks";

import { createProjectShare, deleteProjectShare, fetchProjectShares } from "../lib/api";
import type { ProjectShareOut } from "../types";

// Sprint-017 E7 (ADR-003 §5, design §5) — the "Who's helping" data layer.
// Owner-only: `GET /projects/{id}/shares` is `can_share`-gated, so this hook
// is only enabled for a project the caller owns (or admin/api). Exposes
// add ("let someone help") and remove ("take {name} off …") — both
// idempotent server-side.

export interface UseProjectSharesResult {
  shares: ProjectShareOut[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  addHelper: (userId: string) => Promise<ProjectShareOut>;
  removeHelper: (userId: string) => Promise<void>;
}

export function useProjectShares(projectId: string, enabled: boolean): UseProjectSharesResult {
  const [shares, setShares] = useState<ProjectShareOut[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setError(null);
    try {
      const rows = await fetchProjectShares(projectId);
      setShares(rows);
    } catch {
      setError("Couldn't load who's helping.");
    } finally {
      setLoading(false);
    }
  }, [projectId, enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const addHelper = useCallback(
    async (userId: string): Promise<ProjectShareOut> => {
      const created = await createProjectShare(projectId, userId, crypto.randomUUID());
      setShares((prev) =>
        prev.some((s) => s.user_id === created.user_id) ? prev : [...prev, created],
      );
      return created;
    },
    [projectId],
  );

  const removeHelper = useCallback(
    async (userId: string) => {
      setShares((prev) => prev.filter((s) => s.user_id !== userId));
      try {
        await deleteProjectShare(projectId, userId);
      } catch {
        await refresh();
      }
    },
    [projectId, refresh],
  );

  return { shares, loading, error, refresh, addHelper, removeHelper };
}
